import React from 'react';
import {
  Button,
  TextField,
  Checkbox,
  Select,
  EditableBadge,
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  Divider,
  StatusIndicator,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  Flex,
  Stack,
  Box,
  Heading,
  Text,
  Menu,
  MenuItem,
  MenuTrigger,
  MenuSeparator,
  SplitButton,
  TagGroup,
  MultiSelectComboBox,
  Tooltip,
  TooltipTrigger,
  DataTable,
  SideNav,
  SideNavGroup,
  SideNavLink,
  Breadcrumbs,
  Breadcrumb,
  Progress,
  MediaPlayer,
  useToast,
  LineChart,
  BarChart,
  PieChart,
  AreaChart,
  GaugeChart,
  DatePicker,
  DateRangePicker,
  TimeField,
  DateTimePicker,
  DateTimeRangePicker,
  CalendarDateTime,
  parseTime,
  today,
  getLocalTimeZone,
  toCalendarDateTime,
  TagBadge,
  TagPicker,
  TAG_COLOR_OPTIONS,
  House,
  User,
  Settings,
  ChartBar,
  ChatCircle,
  Plus,
  Download,
  Heart,
  Star,
  MagnifyingGlass,
  Bell,
  Calendar,
  Trash,
  Edit,
  Eye,
  Share,
  ShoppingCart,
  iconSizes,
  RichTextEditor,
  TOKEN_COLORS,
  type EditableBadgeOption,
  type MultiSelectOption,
  type DateValue,
  type TimeValue,
  type DateTimeRangeValue,
  type TagColor,
  type TagOption,
  type SelectOption,
  type TokenPattern,
  type SuggestionTrigger,
  type SuggestionItem,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import { MACRO_CATALOG, MACRO_PATTERN, VARIABLE_PATTERN } from '@chatsdet/types';
import { createMacroSuggestionRenderer } from '../components/node-config/MacroSuggestionPopover';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { InteractionStateGrid } from './ui-demo/InteractionStateGrid';

function UIDemoContent() {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedOption, setSelectedOption] = React.useState<string>('');
  const [isChecked, setIsChecked] = React.useState(false);
  const [textValue, setTextValue] = React.useState('');

  // Date/Time state
  const [selectedDate, setSelectedDate] = React.useState<DateValue | null>(
    today(getLocalTimeZone())
  );
  const [selectedDateRange, setSelectedDateRange] = React.useState<{
    start: DateValue;
    end: DateValue;
  } | null>(null);
  const [selectedTime, setSelectedTime] = React.useState<TimeValue | null>(parseTime('09:00'));
  const [selectedDateTime, setSelectedDateTime] = React.useState<CalendarDateTime | null>(
    toCalendarDateTime(today(getLocalTimeZone()), parseTime('14:30'))
  );
  const [selectedDateTimeRange, setSelectedDateTimeRange] =
    React.useState<DateTimeRangeValue | null>(null);
  const [filterDemoChips, setFilterDemoChips] = React.useState([
    { id: 'status', label: 'Status: Open' },
    { id: 'severity', label: 'Severity: High' },
    { id: 'owner', label: 'Owner: Growth team' },
  ]);

  // Tag demo state (in-memory)
  const [demoTags, setDemoTags] = React.useState<TagOption[]>([
    { id: '1', name: 'Bug', color: 'red' },
    { id: '2', name: 'Enhancement', color: 'blue' },
    { id: '3', name: 'Documentation', color: 'purple' },
    { id: '4', name: 'Performance', color: 'orange' },
    { id: '5', name: 'Security', color: 'pink' },
  ]);
  const [selectedDemoTags, setSelectedDemoTags] = React.useState<TagOption[]>([
    { id: '1', name: 'Bug', color: 'red' },
    { id: '3', name: 'Documentation', color: 'purple' },
  ]);

  const handleCreateDemoTag = React.useCallback(
    async (name: string, color: TagColor): Promise<TagOption> => {
      const newTag: TagOption = { id: String(Date.now()), name, color };
      setDemoTags((prev) => [...prev, newTag]);
      return newTag;
    },
    []
  );

  // MultiSelectComboBox state
  const multiSelectOptions: MultiSelectOption[] = [
    { id: 'react', label: 'React', description: 'A JavaScript library for building UIs' },
    { id: 'typescript', label: 'TypeScript', description: 'Typed superset of JavaScript' },
    { id: 'tailwind', label: 'Tailwind CSS', description: 'Utility-first CSS framework' },
    { id: 'nextjs', label: 'Next.js', description: 'React framework for production' },
    { id: 'vue', label: 'Vue.js', description: 'Progressive JavaScript framework' },
    { id: 'svelte', label: 'Svelte', description: 'Cybernetically enhanced web apps' },
    { id: 'angular', label: 'Angular', description: 'Platform for web applications' },
    { id: 'solid', label: 'SolidJS', description: 'Simple and performant reactivity' },
  ];
  const [selectedTechnologies, setSelectedTechnologies] = React.useState<MultiSelectOption[]>([
    multiSelectOptions[0],
    multiSelectOptions[1],
  ]);
  const filterDemoResults = 128 as number;

  // EditableBadge state
  const [editableSeverity, setEditableSeverity] = React.useState<
    'low' | 'medium' | 'high' | 'critical'
  >('medium');
  const [editableStatus, setEditableStatus] = React.useState<
    'open' | 'in_progress' | 'resolved' | 'closed'
  >('open');

  const severityOptions: EditableBadgeOption<'low' | 'medium' | 'high' | 'critical'>[] = [
    { id: 'low', label: 'Low' },
    { id: 'medium', label: 'Medium' },
    { id: 'high', label: 'High' },
    { id: 'critical', label: 'Critical' },
  ];

  const statusOptions: EditableBadgeOption<'open' | 'in_progress' | 'resolved' | 'closed'>[] = [
    { id: 'open', label: 'Open' },
    { id: 'in_progress', label: 'In Progress' },
    { id: 'resolved', label: 'Resolved' },
    { id: 'closed', label: 'Closed' },
  ];

  const getSeverityVariant = (severity: 'low' | 'medium' | 'high' | 'critical') => {
    switch (severity) {
      case 'critical':
        return 'destructive' as const;
      case 'high':
        return 'warning' as const;
      case 'medium':
        return 'secondary' as const;
      case 'low':
        return 'outline' as const;
    }
  };

  const getStatusVariant = (status: 'open' | 'in_progress' | 'resolved' | 'closed') => {
    switch (status) {
      case 'open':
        return 'secondary' as const;
      case 'in_progress':
        return 'warning' as const;
      case 'resolved':
        return 'success' as const;
      case 'closed':
        return 'outline' as const;
    }
  };
  const filterDemoRemove = React.useCallback((id: string) => {
    setFilterDemoChips((chips) => chips.filter((chip) => chip.id !== id));
  }, []);
  const filterDemoReset = React.useCallback(() => {
    setFilterDemoChips([]);
  }, []);

  const selectOptions: SelectOption[] = [
    { id: '1', label: 'Option 1' },
    { id: '2', label: 'Option 2' },
    { id: '3', label: 'Option 3' },
  ];

  const sortDemoOptions: SelectOption[] = [
    { id: 'cherry', label: 'Cherry' },
    { id: 'banana', label: 'Banana' },
    { id: 'apple', label: 'Apple' },
    { id: 'elderberry', label: 'Elderberry' },
    { id: 'date', label: 'Date' },
  ];

  const channelOptions: SelectOption[] = [
    { id: 'general', label: '#general' },
    { id: 'random', label: '#random' },
    { id: 'engineering', label: '#engineering' },
    { id: 'design', label: '#design' },
    { id: 'product', label: '#product' },
    { id: 'marketing', label: '#marketing' },
    { id: 'sales', label: '#sales' },
    { id: 'support', label: '#support' },
    { id: 'qa-alerts', label: '#qa-alerts' },
    { id: 'deployments', label: '#deployments' },
    { id: 'incidents', label: '#incidents' },
    { id: 'standup', label: '#standup' },
    { id: 'watercooler', label: '#watercooler' },
    { id: 'announcements', label: '#announcements' },
    { id: 'feedback', label: '#feedback' },
  ];

  const [sortedSelection, setSortedSelection] = React.useState<string>('');
  const [searchableSelection, setSearchableSelection] = React.useState<string>('');
  const [unsortedSelection, setUnsortedSelection] = React.useState<string>('');

  const tags = [
    { id: '1', label: 'React' },
    { id: '2', label: 'TypeScript' },
    { id: '3', label: 'Tailwind' },
  ];

  // Sample data for the table
  type Person = {
    id: string;
    name: string;
    email: string;
    role: string;
    status: 'active' | 'inactive' | 'pending';
    team: string;
    location: string;
    lastActive: string;
    summary: string;
    skills: string[];
  };

  const sampleData: Person[] = [
    {
      id: '1',
      name: 'John Doe',
      email: 'john@example.com',
      role: 'Developer',
      status: 'active',
      team: 'Growth',
      location: 'New York, USA',
      lastActive: '2h ago',
      summary: 'Working on the onboarding flow refactor and performance dashboards.',
      skills: ['React', 'TypeScript', 'Effect'],
    },
    {
      id: '2',
      name: 'Jane Smith',
      email: 'jane@example.com',
      role: 'Designer',
      status: 'active',
      team: 'Product Design',
      location: 'San Francisco, USA',
      lastActive: '25m ago',
      summary: 'Iterating on design tokens, accessibility audits, and UI polish.',
      skills: ['Figma', 'Design Systems', 'Accessibility'],
    },
    {
      id: '3',
      name: 'Bob Johnson',
      email: 'bob@example.com',
      role: 'Manager',
      status: 'inactive',
      team: 'Ops',
      location: 'Austin, USA',
      lastActive: '5d ago',
      summary: 'Out on PTO; typically leads ops automation initiatives.',
      skills: ['Process', 'Automation', 'Stakeholder comms'],
    },
    {
      id: '4',
      name: 'Alice Brown',
      email: 'alice@example.com',
      role: 'Developer',
      status: 'pending',
      team: 'Platform',
      location: 'Remote, UK',
      lastActive: 'Pending onboarding',
      summary: 'Joining the platform team to work on auth + infra hardening.',
      skills: ['Node', 'Infra', 'Security'],
    },
    {
      id: '5',
      name: 'Charlie Wilson',
      email: 'charlie@example.com',
      role: 'Designer',
      status: 'active',
      team: 'Brand',
      location: 'Portland, USA',
      lastActive: '1h ago',
      summary: 'Driving a refreshed visual system and storytelling assets.',
      skills: ['Brand', 'Illustration', 'Motion'],
    },
  ];

  const columns: ColumnDef<StockFeatures, Person>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
    },
    {
      accessorKey: 'email',
      header: 'Email',
    },
    {
      accessorKey: 'role',
      header: 'Role',
    },
    {
      accessorKey: 'team',
      header: 'Team',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status') as string;
        const variant =
          status === 'active' ? 'success' : status === 'inactive' ? 'destructive' : 'warning';
        return <TagBadge name={status} color={badgeVariantToTagColor(variant)} hideDot />;
      },
    },
  ];

  return (
    <Box padding="lg">
      <Stack gap="xl">
        <div>
          <Heading level={1}>UI Component Library Demo</Heading>
          <Text color="muted" className="mt-2">
            This is a demonstration of the new UI library with warm sepia-toned colors, React Aria
            Components, and Tailwind CSS.
          </Text>
        </div>
        <Text size="sm" color="muted" className="mt-2">
          ✨ Now featuring warmer gray tones with subtle sepia undertones for a more comfortable
          viewing experience
        </Text>

        <Divider />

        <section>
          <Heading level={2}>Buttons</Heading>

          <div className="mt-4 space-y-6">
            <div>
              <Text weight="medium" className="mb-3">
                Button Variants
              </Text>
              <Flex gap="md" wrap>
                <Button variant="primary">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="destructive">Destructive</Button>
                <Button variant="link">Link</Button>
              </Flex>
            </div>

            <div>
              <Text weight="medium" className="mb-3">
                Buttons with Icons
              </Text>
              <Flex gap="md" wrap>
                <Button variant="primary" iconLeft={<Plus size={16} />}>
                  Add Item
                </Button>
                <Button variant="secondary" iconRight={<Download size={16} />}>
                  Download
                </Button>
                <Button variant="outline" iconLeft={<Heart size={16} />}>
                  Like
                </Button>
                <Button variant="ghost" iconLeft={<Share size={16} />}>
                  Share
                </Button>
              </Flex>
            </div>

            <div>
              <Text weight="medium" className="mb-3">
                Icon-Only Buttons
              </Text>
              <Flex gap="md" wrap>
                <Button variant="primary" iconOnly>
                  <Plus size={16} />
                </Button>
                <Button variant="secondary" iconOnly>
                  <Edit size={16} />
                </Button>
                <Button variant="outline" iconOnly>
                  <Eye size={16} />
                </Button>
                <Button variant="ghost" iconOnly>
                  <Heart size={16} />
                </Button>
                <Button variant="destructive" iconOnly>
                  <Trash size={16} />
                </Button>
              </Flex>
            </div>

            <div>
              <Text weight="medium" className="mb-3">
                Button Sizes
              </Text>
              <Flex gap="md" wrap align="center">
                <Button size="sm" iconLeft={<Star size={14} />}>
                  Small
                </Button>
                <Button size="md" iconLeft={<Star size={16} />}>
                  Medium
                </Button>
                <Button size="lg" iconLeft={<Star size={18} />}>
                  Large
                </Button>
              </Flex>
            </div>

            <div>
              <Text weight="medium" className="mb-3">
                Split Buttons
              </Text>
              <Flex gap="md" wrap align="center">
                <SplitButton variant="primary" onPress={() => console.log('Save clicked')}>
                  Save
                  <SplitButton.Item key="draft" onAction={() => console.log('Save as Draft')}>
                    Save as Draft
                  </SplitButton.Item>
                  <SplitButton.Item key="template" onAction={() => console.log('Save as Template')}>
                    Save as Template
                  </SplitButton.Item>
                  <SplitButton.Separator />
                  <SplitButton.Item
                    key="discard"
                    destructive
                    onAction={() => console.log('Discard')}
                  >
                    Discard
                  </SplitButton.Item>
                </SplitButton>

                <SplitButton
                  variant="secondary"
                  iconLeft={<Download size={16} />}
                  onPress={() => console.log('Export clicked')}
                >
                  Export
                  <SplitButton.Item key="csv" onAction={() => console.log('Export CSV')}>
                    Export as CSV
                  </SplitButton.Item>
                  <SplitButton.Item key="json" onAction={() => console.log('Export JSON')}>
                    Export as JSON
                  </SplitButton.Item>
                  <SplitButton.Item key="pdf" onAction={() => console.log('Export PDF')}>
                    Export as PDF
                  </SplitButton.Item>
                </SplitButton>

                <SplitButton
                  variant="outline"
                  size="sm"
                  onPress={() => console.log('Create clicked')}
                >
                  Create
                  <SplitButton.Item key="folder" onAction={() => console.log('New Folder')}>
                    New Folder
                  </SplitButton.Item>
                  <SplitButton.Item key="file" onAction={() => console.log('New File')}>
                    New File
                  </SplitButton.Item>
                </SplitButton>
              </Flex>
            </div>
          </div>
        </section>

        <Divider />

        <section>
          <InteractionStateGrid />
        </section>

        <Divider />

        <section>
          <Heading level={2}>Typography</Heading>
          <Text color="muted" className="mt-2 mb-6">
            Text and heading components with various styles and sizes
          </Text>

          <div className="space-y-8">
            {/* Headings */}
            <div>
              <Text weight="medium" className="mb-4">
                Headings
              </Text>
              <Stack gap="md">
                <Heading level={1}>Heading Level 1 - The quick brown fox</Heading>
                <Heading level={2}>Heading Level 2 - The quick brown fox</Heading>
                <Heading level={3}>Heading Level 3 - The quick brown fox</Heading>
                <Heading level={4}>Heading Level 4 - The quick brown fox</Heading>
                <Heading level={5}>Heading Level 5 - The quick brown fox</Heading>
                <Heading level={6}>Heading Level 6 - The quick brown fox</Heading>
              </Stack>
            </div>

            {/* Text Sizes */}
            <div>
              <Text weight="medium" className="mb-4">
                Text Sizes
              </Text>
              <Stack gap="sm">
                <Text size="xl">
                  Extra Large Text - The quick brown fox jumps over the lazy dog
                </Text>
                <Text size="lg">Large Text - The quick brown fox jumps over the lazy dog</Text>
                <Text size="md">
                  Base Text (default) - The quick brown fox jumps over the lazy dog
                </Text>
                <Text size="sm">Small Text - The quick brown fox jumps over the lazy dog</Text>
                <Text size="xs">
                  Extra Small Text - The quick brown fox jumps over the lazy dog
                </Text>
              </Stack>
            </div>

            {/* Text Weights */}
            <div>
              <Text weight="medium" className="mb-4">
                Text Weights
              </Text>
              <Stack gap="sm">
                <Text weight="normal">
                  Light Weight - The quick brown fox jumps over the lazy dog
                </Text>
                <Text weight="normal">
                  Regular Weight (default) - The quick brown fox jumps over the lazy dog
                </Text>
                <Text weight="medium">
                  Medium Weight - The quick brown fox jumps over the lazy dog
                </Text>
                <Text weight="semibold">
                  Semibold Weight - The quick brown fox jumps over the lazy dog
                </Text>
                <Text weight="bold">Bold Weight - The quick brown fox jumps over the lazy dog</Text>
              </Stack>
            </div>

            {/* Text Colors */}
            <div>
              <Text weight="medium" className="mb-4">
                Text Colors
              </Text>
              <Stack gap="sm">
                <Text color="default">
                  Default Color - The quick brown fox jumps over the lazy dog
                </Text>
                <Text color="muted">Muted Color - The quick brown fox jumps over the lazy dog</Text>
                <Text color="primary">
                  Primary Color - The quick brown fox jumps over the lazy dog
                </Text>
                <Text color="destructive">
                  Destructive Color - The quick brown fox jumps over the lazy dog
                </Text>
                <Text color="success">
                  Success Color - The quick brown fox jumps over the lazy dog
                </Text>
                <Text color="warning">
                  Warning Color - The quick brown fox jumps over the lazy dog
                </Text>
                <Text color="info">Info Color - The quick brown fox jumps over the lazy dog</Text>
              </Stack>
            </div>

            {/* Text Alignment */}
            <div>
              <Text weight="medium" className="mb-4">
                Text Alignment
              </Text>
              <Stack gap="sm">
                <div className="p-4 border border-border rounded-lg">
                  <Text className="text-left">
                    Left aligned text - The quick brown fox jumps over the lazy dog. Lorem ipsum
                    dolor sit amet, consectetur adipiscing elit.
                  </Text>
                </div>
                <div className="p-4 border border-border rounded-lg">
                  <Text className="text-center">
                    Center aligned text - The quick brown fox jumps over the lazy dog. Lorem ipsum
                    dolor sit amet, consectetur adipiscing elit.
                  </Text>
                </div>
                <div className="p-4 border border-border rounded-lg">
                  <Text className="text-right">
                    Right aligned text - The quick brown fox jumps over the lazy dog. Lorem ipsum
                    dolor sit amet, consectetur adipiscing elit.
                  </Text>
                </div>
              </Stack>
            </div>

            {/* Combined Examples */}
            <div>
              <Text weight="medium" className="mb-4">
                Combined Examples
              </Text>
              <Stack gap="md">
                <Card>
                  <CardHeader>
                    <Heading level={3}>Article Title</Heading>
                    <Text size="sm" color="muted" className="mt-1">
                      Published on December 2, 2024 · 5 min read
                    </Text>
                  </CardHeader>
                  <CardContent>
                    <Text className="mb-3">
                      This is a paragraph of body text demonstrating the default text styling. The
                      warm sepia tones create a comfortable reading experience that's easy on the
                      eyes.
                    </Text>
                    <Text size="sm" color="muted">
                      This is a smaller, muted text that could be used for captions, descriptions,
                      or secondary information. It provides good contrast hierarchy while
                      maintaining readability.
                    </Text>
                  </CardContent>
                </Card>

                <Card className="bg-muted">
                  <CardContent>
                    <Heading level={4} className="mb-2">
                      Important Notice
                    </Heading>
                    <Text size="lg" weight="medium" color="warning" className="mb-2">
                      This is a warning message with larger, bold text.
                    </Text>
                    <Text size="sm">
                      Additional details about the warning go here in smaller text to provide
                      context without overwhelming the main message.
                    </Text>
                  </CardContent>
                </Card>
              </Stack>
            </div>
          </div>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Form Components</Heading>
          <Stack gap="md" className="mt-4 max-w-md">
            <div>
              <Text weight="medium" className="mb-2">
                TextField Variants
              </Text>
              <Stack gap="sm">
                <TextField
                  label="Default Variant"
                  placeholder="Default background"
                  variant="default"
                  description="Default variant with background"
                />
                <TextField
                  label="Filled Variant"
                  placeholder="Filled background"
                  variant="filled"
                  description="Filled variant with muted background"
                />
                <TextField
                  label="Ghost Variant"
                  placeholder="No background"
                  variant="ghost"
                  description="Ghost variant with no background"
                />
              </Stack>
            </div>

            <TextField
              label="Text Field"
              placeholder="Enter some text"
              value={textValue}
              onChange={setTextValue}
              description="This is a helper text"
            />

            <Select
              label="Select Field"
              placeholder="Choose an option"
              items={selectOptions}
              selectedKey={selectedOption}
              onSelectionChange={(key) => setSelectedOption(String(key))}
            />

            <Divider />
            <Heading level={3}>Searchable Select</Heading>
            <Text color="muted" className="mb-4">
              Select supports opt-in sorting and a searchable filter input for long lists.
            </Text>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Text className="font-medium text-sm">Unsorted (default)</Text>
                <Select
                  label="Fruit"
                  placeholder="Pick a fruit"
                  items={sortDemoOptions}
                  selectedKey={unsortedSelection}
                  onSelectionChange={(key) => setUnsortedSelection(String(key))}
                />
              </div>
              <div className="space-y-4">
                <Text className="font-medium text-sm">Sorted (alphabetical)</Text>
                <Select
                  label="Fruit"
                  placeholder="Pick a fruit"
                  items={sortDemoOptions}
                  sort
                  selectedKey={sortedSelection}
                  onSelectionChange={(key) => setSortedSelection(String(key))}
                />
              </div>
            </div>

            <Select
              label="Slack Channel"
              placeholder="Choose a channel"
              items={channelOptions}
              sort
              searchPlaceholder="Search channels..."
              selectedKey={searchableSelection}
              onSelectionChange={(key) => setSearchableSelection(String(key))}
            />

            <Checkbox
              isSelected={isChecked}
              onChange={setIsChecked}
              label="Checkbox Label"
              description="This is a checkbox with description"
            />
          </Stack>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Date & Time Pickers</Heading>
          <Text color="muted" className="mt-2 mb-6">
            Accessible date and time components with keyboard navigation, calendar popups, and
            timezone support
          </Text>

          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* DatePicker */}
              <Card>
                <CardHeader>
                  <Heading level={4}>DatePicker</Heading>
                  <Text size="sm" color="muted">
                    Single date selection with calendar popup
                  </Text>
                </CardHeader>
                <CardContent>
                  <Stack gap="md">
                    <DatePicker
                      label="Start Date"
                      value={selectedDate}
                      onChange={setSelectedDate}
                      description="Select a start date for your project"
                    />

                    <DatePicker
                      label="Deadline (Optional)"
                      description="Leave blank if no deadline"
                    />

                    <DatePicker
                      label="Event Date"
                      value={selectedDate}
                      onChange={setSelectedDate}
                      isDisabled
                      description="This field is disabled"
                    />

                    {selectedDate && (
                      <div className="p-3 bg-muted rounded-md">
                        <Text size="sm" weight="medium">
                          Selected Date:
                        </Text>
                        <Text size="sm" color="muted" className="font-mono mt-1">
                          {selectedDate.toString()}
                        </Text>
                      </div>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {/* TimeField */}
              <Card>
                <CardHeader>
                  <Heading level={4}>TimeField</Heading>
                  <Text size="sm" color="muted">
                    Time input with keyboard-navigable segments
                  </Text>
                </CardHeader>
                <CardContent>
                  <Stack gap="md">
                    <TimeField
                      label="Start Time"
                      value={selectedTime}
                      onChange={setSelectedTime}
                      description="Enter time in 24-hour format"
                    />

                    <TimeField label="End Time" description="Optional end time" />

                    <TimeField
                      label="Reminder Time"
                      value={selectedTime}
                      onChange={setSelectedTime}
                      isDisabled
                      description="This field is disabled"
                    />

                    {selectedTime && (
                      <div className="p-3 bg-muted rounded-md">
                        <Text size="sm" weight="medium">
                          Selected Time:
                        </Text>
                        <Text size="sm" color="muted" className="font-mono mt-1">
                          {selectedTime.toString()}
                        </Text>
                      </div>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </div>

            {/* DateRangePicker - Full Width */}
            <Card>
              <CardHeader>
                <Heading level={4}>DateRangePicker</Heading>
                <Text size="sm" color="muted">
                  Select a date range with start and end dates
                </Text>
              </CardHeader>
              <CardContent>
                <div className="max-w-2xl">
                  <Stack gap="md">
                    <DateRangePicker
                      label="Reporting Period"
                      value={selectedDateRange}
                      onChange={setSelectedDateRange}
                      description="Select the date range for your report"
                    />

                    <DateRangePicker
                      label="Vacation Dates"
                      description="Select start and end dates for your time off"
                    />

                    {selectedDateRange && (
                      <div className="p-3 bg-muted rounded-md">
                        <Text size="sm" weight="medium">
                          Selected Range:
                        </Text>
                        <Text size="sm" color="muted" className="font-mono mt-1">
                          Start: {selectedDateRange.start.toString()}
                        </Text>
                        <Text size="sm" color="muted" className="font-mono">
                          End: {selectedDateRange.end.toString()}
                        </Text>
                      </div>
                    )}
                  </Stack>
                </div>
              </CardContent>
            </Card>

            {/* DateTimePicker */}
            <Card>
              <CardHeader>
                <Heading level={4}>DateTimePicker</Heading>
                <Text size="sm" color="muted">
                  Combined date and time selection in a single component with time picker in
                  calendar popover
                </Text>
              </CardHeader>
              <CardContent>
                <div className="max-w-2xl">
                  <Stack gap="md">
                    <DateTimePicker
                      label="Event Date & Time"
                      value={selectedDateTime}
                      onChange={setSelectedDateTime}
                      description="Select both date and time from the calendar popover"
                    />

                    <DateTimePicker label="Meeting Schedule" description="Optional meeting time" />

                    {selectedDateTime && (
                      <div className="p-4 bg-muted rounded-md">
                        <Text size="sm" weight="medium" className="mb-2">
                          Selected Date & Time:
                        </Text>
                        <Text size="sm" color="muted" className="font-mono">
                          {selectedDateTime.toString()}
                        </Text>
                        <Text size="sm" color="muted" className="mt-1">
                          {selectedDateTime.toDate(getLocalTimeZone()).toLocaleString()}
                        </Text>
                      </div>
                    )}
                  </Stack>
                </div>
              </CardContent>
            </Card>

            {/* DateTimeRangePicker */}
            <Card>
              <CardHeader>
                <Heading level={4}>DateTimeRangePicker</Heading>
                <Text size="sm" color="muted">
                  Select date and time range with calendar and dual time controls
                </Text>
              </CardHeader>
              <CardContent>
                <div className="max-w-2xl">
                  <Stack gap="md">
                    <DateTimeRangePicker
                      label="Event Duration"
                      value={selectedDateTimeRange}
                      onChange={setSelectedDateTimeRange}
                      description="Select start and end date/time for your event"
                    />

                    <DateTimeRangePicker
                      label="Availability Window"
                      description="When are you available?"
                    />

                    {selectedDateTimeRange && (
                      <div className="p-4 bg-muted rounded-md">
                        <Text size="sm" weight="medium" className="mb-2">
                          Selected Date & Time Range:
                        </Text>
                        <div className="space-y-1">
                          <Text size="sm" color="muted" className="font-mono">
                            Start: {selectedDateTimeRange.start.toString()}
                          </Text>
                          <Text size="sm" color="muted" className="font-mono">
                            End: {selectedDateTimeRange.end.toString()}
                          </Text>
                          <div className="mt-2 pt-2 border-t border-border">
                            <Text size="sm" color="muted">
                              {selectedDateTimeRange.start
                                .toDate(getLocalTimeZone())
                                .toLocaleString()}
                              {' → '}
                              {selectedDateTimeRange.end
                                .toDate(getLocalTimeZone())
                                .toLocaleString()}
                            </Text>
                          </div>
                        </div>
                      </div>
                    )}
                  </Stack>
                </div>
              </CardContent>
            </Card>

            {/* Separate Date + Time (Alternative Pattern) */}
            <Card>
              <CardHeader>
                <Heading level={4}>Separate Date + Time Fields</Heading>
                <Text size="sm" color="muted">
                  Alternative pattern: use DatePicker and TimeField as separate components
                </Text>
              </CardHeader>
              <CardContent>
                <div className="max-w-2xl">
                  <Stack gap="md">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <DatePicker
                        label="Event Date"
                        value={selectedDate}
                        onChange={setSelectedDate}
                      />
                      <TimeField
                        label="Event Time"
                        value={selectedTime}
                        onChange={setSelectedTime}
                      />
                    </div>

                    {selectedDate && selectedTime && (
                      <div className="p-4 bg-muted rounded-md">
                        <Text size="sm" weight="medium" className="mb-2">
                          Combined Date & Time:
                        </Text>
                        <Text size="sm" color="muted">
                          {selectedDate.toString()} at {selectedTime.toString()}
                        </Text>
                      </div>
                    )}
                  </Stack>
                </div>
              </CardContent>
            </Card>

            {/* Features & Usage */}
            <Card>
              <CardHeader>
                <Heading level={4}>Features & Usage</Heading>
                <Text size="sm" color="muted">
                  Key features and implementation details
                </Text>
              </CardHeader>
              <CardContent>
                <Stack gap="md">
                  <div>
                    <Text weight="medium" className="mb-2">
                      ✨ Features:
                    </Text>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      <li>Full keyboard navigation (arrow keys, Tab, Enter)</li>
                      <li>Screen reader accessible</li>
                      <li>Timezone-aware date handling</li>
                      <li>Segment-based input (click to edit individual parts)</li>
                      <li>Consistent browser experience (no native date inputs)</li>
                      <li>Validation and error states</li>
                      <li>Min/max date constraints support</li>
                    </ul>
                  </div>

                  <Divider />

                  <div>
                    <Text weight="medium" className="mb-2">
                      📖 Basic Usage:
                    </Text>
                    <div className="bg-muted p-3 rounded-md font-mono text-xs space-y-2">
                      <div>
                        <Text size="xs" color="muted">
                          Import date components:
                        </Text>
                        <Text
                          size="xs"
                          className="mt-1"
                        >{`import { DatePicker, TimeField, today, TagBadge } from '@chatsdet/ui';`}</Text>
                      </div>
                      <div className="mt-3">
                        <Text size="xs" color="muted">
                          Import date-time components:
                        </Text>
                        <Text
                          size="xs"
                          className="mt-1"
                        >{`import { DateTimePicker, DateTimeRangePicker, CalendarDateTime, TagBadge } from '@chatsdet/ui';`}</Text>
                      </div>
                      <div className="mt-3">
                        <Text size="xs" color="muted">
                          Initialize state:
                        </Text>
                        <Text
                          size="xs"
                          className="mt-1"
                        >{`const [dateTime, setDateTime] = useState<CalendarDateTime | null>(null);`}</Text>
                      </div>
                      <div className="mt-3">
                        <Text size="xs" color="muted">
                          Use component:
                        </Text>
                        <Text
                          size="xs"
                          className="mt-1"
                        >{`<DateTimePicker label="Event" value={dateTime} onChange={setDateTime} />`}</Text>
                      </div>
                    </div>
                  </div>
                </Stack>
              </CardContent>
            </Card>
          </div>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Cards</Heading>
          <Flex gap="md" wrap className="mt-4">
            <Card className="w-80">
              <CardHeader>
                <Heading level={3}>Card Title</Heading>
                <Text color="muted" size="sm">
                  Card subtitle goes here
                </Text>
              </CardHeader>
              <CardContent>
                <Text>This is the card content. You can put any content here.</Text>
              </CardContent>
              <CardFooter>
                <Flex gap="sm" justify="end" className="w-full">
                  <Button variant="outline" size="sm">
                    Cancel
                  </Button>
                  <Button variant="primary" size="sm">
                    Save
                  </Button>
                </Flex>
              </CardFooter>
            </Card>
          </Flex>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Badges & Status</Heading>
          <Stack gap="md" className="mt-4">
            <Flex gap="sm" wrap>
              <TagBadge name="Default" color="blue" hideDot />
              <TagBadge name="Secondary" color="gray" hideDot />
              <TagBadge name="Destructive" color="red" hideDot />
              <TagBadge name="Outline" color="gray" hideDot />
              <TagBadge name="Success" color="green" hideDot />
              <TagBadge name="Warning" color="yellow" hideDot />
            </Flex>

            <Flex gap="md" wrap align="center">
              <StatusIndicator variant="positive">Online</StatusIndicator>
              <StatusIndicator variant="negative">Offline</StatusIndicator>
              <StatusIndicator variant="notice">Away</StatusIndicator>
              <StatusIndicator variant="info" pulse>
                Processing
              </StatusIndicator>
            </Flex>
          </Stack>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Editable Badges</Heading>
          <Text color="muted" className="mt-2 mb-4">
            Badge-styled dropdowns for inline editing of categorical values like severity, status,
            or category
          </Text>

          <div className="space-y-6">
            <div>
              <Text weight="medium" className="mb-3">
                Basic EditableBadge
              </Text>
              <Flex gap="md" wrap align="center">
                <div className="space-y-2">
                  <Text size="sm" color="muted">
                    Severity (with dynamic variant)
                  </Text>
                  <EditableBadge
                    value={editableSeverity}
                    options={severityOptions}
                    onChange={setEditableSeverity}
                    getVariant={getSeverityVariant}
                    aria-label="Select severity"
                  />
                </div>
                <div className="space-y-2">
                  <Text size="sm" color="muted">
                    Status (with dynamic variant)
                  </Text>
                  <EditableBadge
                    value={editableStatus}
                    options={statusOptions}
                    onChange={setEditableStatus}
                    getVariant={getStatusVariant}
                    aria-label="Select status"
                  />
                </div>
              </Flex>
            </div>

            <div>
              <Text weight="medium" className="mb-3">
                Fixed Variant
              </Text>
              <Flex gap="md" wrap align="center">
                <EditableBadge
                  value={editableSeverity}
                  options={severityOptions}
                  onChange={setEditableSeverity}
                  variant="secondary"
                  aria-label="Select severity (fixed variant)"
                />
              </Flex>
            </div>

            <div>
              <Text weight="medium" className="mb-3">
                Loading State
              </Text>
              <Flex gap="md" wrap align="center">
                <EditableBadge
                  value={editableSeverity}
                  options={severityOptions}
                  onChange={setEditableSeverity}
                  getVariant={getSeverityVariant}
                  isLoading
                  aria-label="Loading severity"
                />
              </Flex>
            </div>

            <div>
              <Text weight="medium" className="mb-3">
                Disabled State
              </Text>
              <Flex gap="md" wrap align="center">
                <EditableBadge
                  value={editableSeverity}
                  options={severityOptions}
                  onChange={setEditableSeverity}
                  getVariant={getSeverityVariant}
                  isDisabled
                  aria-label="Disabled severity"
                />
              </Flex>
            </div>

            <Card className="mt-4">
              <CardHeader>
                <Heading level={4}>Current Values</Heading>
              </CardHeader>
              <CardContent>
                <Flex gap="lg">
                  <div>
                    <Text size="sm" color="muted">
                      Severity:
                    </Text>
                    <Text weight="medium">{editableSeverity}</Text>
                  </div>
                  <div>
                    <Text size="sm" color="muted">
                      Status:
                    </Text>
                    <Text weight="medium">{editableStatus}</Text>
                  </div>
                </Flex>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Heading level={4}>Usage</Heading>
                <Text size="sm" color="muted">
                  Key features and implementation details
                </Text>
              </CardHeader>
              <CardContent>
                <Stack gap="md">
                  <div>
                    <Text weight="medium" className="mb-2">
                      Features:
                    </Text>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      <li>Looks like a Badge but behaves like a Select</li>
                      <li>Dynamic variant coloring via `getVariant` prop</li>
                      <li>Loading spinner for async operations</li>
                      <li>Prevents parent click events (works in tables)</li>
                      <li>Fully accessible via react-aria-components</li>
                    </ul>
                  </div>

                  <Divider />

                  <div>
                    <Text weight="medium" className="mb-2">
                      Basic Usage:
                    </Text>
                    <div className="bg-muted p-3 rounded-md font-mono text-xs space-y-2">
                      <div>
                        <Text size="xs" color="muted">
                          Import:
                        </Text>
                        <Text
                          size="xs"
                          className="mt-1"
                        >{`import { EditableBadge, type EditableBadgeOption, TagBadge } from '@chatsdet/ui';`}</Text>
                      </div>
                      <div className="mt-3">
                        <Text size="xs" color="muted">
                          Component:
                        </Text>
                        <Text
                          size="xs"
                          className="mt-1"
                        >{`<EditableBadge value={value} options={options} onChange={onChange} />`}</Text>
                      </div>
                    </div>
                  </div>
                </Stack>
              </CardContent>
            </Card>
          </div>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Progress</Heading>
          <Text color="muted" className="mt-2 mb-6">
            Animated progress bars with different sizes and variants
          </Text>

          <div className="space-y-6">
            <div>
              <Text weight="medium" className="mb-3">
                Progress Variants
              </Text>
              <Stack gap="md" className="max-w-md">
                <div>
                  <Text size="sm" color="muted" className="mb-1">
                    Default
                  </Text>
                  <Progress value={75} variant="default" />
                </div>
                <div>
                  <Text size="sm" color="muted" className="mb-1">
                    Success
                  </Text>
                  <Progress value={90} variant="success" />
                </div>
                <div>
                  <Text size="sm" color="muted" className="mb-1">
                    Warning
                  </Text>
                  <Progress value={45} variant="warning" />
                </div>
                <div>
                  <Text size="sm" color="muted" className="mb-1">
                    Destructive
                  </Text>
                  <Progress value={25} variant="destructive" />
                </div>
              </Stack>
            </div>

            <div>
              <Text weight="medium" className="mb-3">
                Progress Sizes
              </Text>
              <Stack gap="md" className="max-w-md">
                <div>
                  <Text size="sm" color="muted" className="mb-1">
                    Small
                  </Text>
                  <Progress value={60} size="sm" />
                </div>
                <div>
                  <Text size="sm" color="muted" className="mb-1">
                    Medium (default)
                  </Text>
                  <Progress value={70} size="md" />
                </div>
                <div>
                  <Text size="sm" color="muted" className="mb-1">
                    Large
                  </Text>
                  <Progress value={80} size="lg" />
                </div>
              </Stack>
            </div>

            <div>
              <Text weight="medium" className="mb-3">
                With Label
              </Text>
              <Stack gap="md" className="max-w-md">
                <Progress value={65} variant="success" showLabel />
                <Progress value={42} variant="warning" showLabel />
              </Stack>
            </div>

            <div>
              <Text weight="medium" className="mb-3">
                Without Animation
              </Text>
              <div className="max-w-md">
                <Progress value={55} animate={false} />
              </div>
            </div>

            <div>
              <Text weight="medium" className="mb-3">
                Use Case: Confidence Score
              </Text>
              <Card className="max-w-md">
                <CardContent>
                  <Text size="sm" weight="medium" className="mb-2">
                    AI Confidence Level
                  </Text>
                  <div className="flex items-center gap-2">
                    <Progress value={87} size="sm" variant="success" className="flex-1" />
                    <Text size="sm" className="shrink-0 w-10 text-right">
                      87%
                    </Text>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Media Player</Heading>
          <Text className="mt-2 mb-4">
            A media player component that uses a screenshot/poster image as thumbnail and plays
            video when clicked. Falls back to static image when no video is available.
          </Text>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
            <Card>
              <CardHeader>
                <Text weight="medium">With Video</Text>
              </CardHeader>
              <CardContent>
                <MediaPlayer
                  posterSrc="https://storage.googleapis.com/gtv-videos-bucket/sample/images/BigBuckBunny.jpg"
                  videoSrc="https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4"
                  alt="Big Buck Bunny sample video"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Text weight="medium">Poster Only (No Video)</Text>
              </CardHeader>
              <CardContent>
                <MediaPlayer
                  posterSrc="https://storage.googleapis.com/gtv-videos-bucket/sample/images/ElephantsDream.jpg"
                  videoSrc={null}
                  alt="Elephants Dream poster image"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Text weight="medium">Error State (Broken URL)</Text>
              </CardHeader>
              <CardContent>
                <MediaPlayer
                  posterSrc="https://storage.googleapis.com/gtv-videos-bucket/sample/images/ForBiggerBlazes.jpg"
                  videoSrc="https://example.com/nonexistent-video.mp4"
                  alt="Error state demo"
                />
              </CardContent>
            </Card>
          </div>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Dialog & Menu</Heading>
          <Flex gap="md" className="mt-4">
            <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
              <Button>Open Dialog</Button>
              <Dialog title="Dialog Title" description="This is a dialog description">
                <DialogContent>
                  <Text>Dialog content goes here. You can add any content or form elements.</Text>
                </DialogContent>
                <DialogFooter>
                  <Button variant="outline" onPress={() => setIsOpen(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" onPress={() => setIsOpen(false)}>
                    Confirm
                  </Button>
                </DialogFooter>
              </Dialog>
            </DialogTrigger>

            <MenuTrigger>
              <Button>Open Menu</Button>
              <Menu onAction={(key) => console.log('Selected:', key)}>
                <MenuItem id="edit">Edit</MenuItem>
                <MenuItem id="duplicate">Duplicate</MenuItem>
                <MenuSeparator />
                <MenuItem id="delete" destructive>
                  Delete
                </MenuItem>
              </Menu>
            </MenuTrigger>
          </Flex>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Toast Notifications</Heading>
          <Text color="muted" className="mt-2 mb-4">
            Customizable toast notifications with variants, auto-dismiss, and actions
          </Text>

          <div className="space-y-6">
            <div>
              <Text weight="medium" className="mb-3">
                Toast Variants
              </Text>
              <Flex gap="md" wrap>
                <Button
                  variant="primary"
                  onPress={() => toast.success('Your changes have been saved successfully!')}
                >
                  Success Toast
                </Button>
                <Button
                  variant="destructive"
                  onPress={() => toast.error('An error occurred while processing your request.')}
                >
                  Error Toast
                </Button>
                <Button
                  variant="outline"
                  onPress={() => toast.warning('Your session will expire in 5 minutes.')}
                >
                  Warning Toast
                </Button>
                <Button
                  variant="secondary"
                  onPress={() => toast.info('New features are now available!')}
                >
                  Info Toast
                </Button>
                <Button
                  variant="ghost"
                  onPress={() => toast.default('This is a default notification.')}
                >
                  Default Toast
                </Button>
              </Flex>
            </div>

            <div>
              <Text weight="medium" className="mb-3">
                With Action Button
              </Text>
              <Flex gap="md" wrap>
                <Button
                  variant="primary"
                  onPress={() =>
                    toast.success('File uploaded successfully!', {
                      action: {
                        label: 'View',
                        onClick: () => alert('Viewing file...'),
                      },
                    })
                  }
                >
                  Toast with Action
                </Button>
                <Button
                  variant="outline"
                  onPress={() =>
                    toast.warning('Your changes are unsaved.', {
                      action: {
                        label: 'Save Now',
                        onClick: () => alert('Saving...'),
                      },
                    })
                  }
                >
                  Warning with Action
                </Button>
              </Flex>
            </div>

            <div>
              <Text weight="medium" className="mb-3">
                Custom Duration
              </Text>
              <Flex gap="md" wrap>
                <Button
                  variant="secondary"
                  onPress={() =>
                    toast.info('This toast will dismiss in 2 seconds', { duration: 2000 })
                  }
                >
                  2 Second Toast
                </Button>
                <Button
                  variant="secondary"
                  onPress={() =>
                    toast.info('This toast will stay until dismissed', { duration: Infinity })
                  }
                >
                  Persistent Toast
                </Button>
              </Flex>
            </div>

            <div>
              <Text weight="medium" className="mb-3">
                Custom Icon
              </Text>
              <Flex gap="md" wrap>
                <Button
                  variant="outline"
                  onPress={() =>
                    toast.default('You have a new message!', {
                      icon: <Bell size={20} className="text-primary" />,
                    })
                  }
                >
                  Custom Icon
                </Button>
                <Button
                  variant="outline"
                  onPress={() =>
                    toast.success('Item added to cart', {
                      icon: <ShoppingCart size={20} className="text-green-600" />,
                    })
                  }
                >
                  Cart Icon
                </Button>
              </Flex>
            </div>
          </div>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Tags</Heading>
          <Box className="mt-4">
            <TagGroup
              label="Technologies"
              items={tags}
              onRemove={(id) => console.log('Remove tag:', id)}
            />
          </Box>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Multi-Select ComboBox</Heading>
          <Text color="muted" className="mt-2 mb-6">
            Searchable multi-select with tag display, combining ComboBox filtering with TagGroup
            selection
          </Text>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <Heading level={4}>Controlled Multi-Select</Heading>
                <Text size="sm" color="muted">
                  Select multiple technologies with search filtering
                </Text>
              </CardHeader>
              <CardContent>
                <Stack gap="md">
                  <MultiSelectComboBox
                    label="Technologies"
                    description="Search and select multiple technologies"
                    placeholder="Search technologies..."
                    items={multiSelectOptions}
                    value={selectedTechnologies}
                    onChange={setSelectedTechnologies}
                  />

                  {selectedTechnologies.length > 0 && (
                    <div className="p-3 bg-muted rounded-md">
                      <Text size="sm" weight="medium">
                        Selected ({selectedTechnologies.length}):
                      </Text>
                      <Text size="sm" color="muted" className="mt-1">
                        {selectedTechnologies.map((t) => t.label).join(', ')}
                      </Text>
                    </div>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Heading level={4}>Uncontrolled with Default Value</Heading>
                <Text size="sm" color="muted">
                  Pre-populated selection with onChange callback
                </Text>
              </CardHeader>
              <CardContent>
                <Stack gap="md">
                  <MultiSelectComboBox
                    label="Team Members"
                    placeholder="Add team members..."
                    items={[
                      { id: 'alice', label: 'Alice Chen', description: 'Engineering' },
                      { id: 'bob', label: 'Bob Smith', description: 'Design' },
                      { id: 'carol', label: 'Carol Davis', description: 'Product' },
                      { id: 'david', label: 'David Kim', description: 'Engineering' },
                      { id: 'emma', label: 'Emma Wilson', description: 'QA' },
                    ]}
                    defaultValue={[
                      { id: 'alice', label: 'Alice Chen', description: 'Engineering' },
                    ]}
                    onChange={(items) => console.log('Team changed:', items)}
                  />

                  <Text size="sm" color="muted">
                    Check console for onChange events
                  </Text>
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Heading level={4}>Loading State</Heading>
                <Text size="sm" color="muted">
                  Shows loading indicator while fetching options
                </Text>
              </CardHeader>
              <CardContent>
                <MultiSelectComboBox
                  label="Categories"
                  placeholder="Loading categories..."
                  items={[]}
                  isLoading
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Heading level={4}>Disabled State</Heading>
                <Text size="sm" color="muted">
                  Non-interactive with existing selections shown
                </Text>
              </CardHeader>
              <CardContent>
                <MultiSelectComboBox
                  label="Locked Selection"
                  placeholder="Cannot modify..."
                  items={multiSelectOptions}
                  defaultValue={[multiSelectOptions[0], multiSelectOptions[2]]}
                  isDisabled
                />
              </CardContent>
            </Card>
          </div>

          <Card className="mt-6">
            <CardHeader>
              <Heading level={4}>Features & Usage</Heading>
              <Text size="sm" color="muted">
                Key features and implementation details
              </Text>
            </CardHeader>
            <CardContent>
              <Stack gap="md">
                <div>
                  <Text weight="medium" className="mb-2">
                    Features:
                  </Text>
                  <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                    <li>Searchable dropdown with filtering</li>
                    <li>Selected items shown as removable tags</li>
                    <li>Backspace removes last tag when input is empty</li>
                    <li>Already-selected items filtered from dropdown</li>
                    <li>Controlled and uncontrolled modes</li>
                    <li>Custom item and tag renderers</li>
                  </ul>
                </div>

                <Divider />

                <div>
                  <Text weight="medium" className="mb-2">
                    Basic Usage:
                  </Text>
                  <div className="bg-muted p-3 rounded-md font-mono text-xs space-y-2">
                    <div>
                      <Text size="xs" color="muted">
                        Import:
                      </Text>
                      <Text
                        size="xs"
                        className="mt-1"
                      >{`import { MultiSelectComboBox, TagBadge } from '@chatsdet/ui';`}</Text>
                    </div>
                    <div className="mt-3">
                      <Text size="xs" color="muted">
                        Controlled usage:
                      </Text>
                      <Text
                        size="xs"
                        className="mt-1"
                      >{`<MultiSelectComboBox items={options} value={selected} onChange={setSelected} />`}</Text>
                    </div>
                  </div>
                </div>
              </Stack>
            </CardContent>
          </Card>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Icons</Heading>
          <Text color="muted" className="mt-2 mb-4">
            Phosphor Icons integration with predefined sizes and easy usage
          </Text>

          <div className="space-y-6">
            <div>
              <Text weight="medium" className="mb-3">
                Icon Sizes
              </Text>
              <Flex gap="lg" wrap align="center">
                <div className="flex items-center gap-2">
                  <House size={iconSizes.xs} />
                  <Text size="sm">XS ({iconSizes.xs}px)</Text>
                </div>
                <div className="flex items-center gap-2">
                  <House size={iconSizes.sm} />
                  <Text size="sm">SM ({iconSizes.sm}px)</Text>
                </div>
                <div className="flex items-center gap-2">
                  <House size={iconSizes.md} />
                  <Text size="sm">MD ({iconSizes.md}px)</Text>
                </div>
                <div className="flex items-center gap-2">
                  <House size={iconSizes.lg} />
                  <Text size="sm">LG ({iconSizes.lg}px)</Text>
                </div>
                <div className="flex items-center gap-2">
                  <House size={iconSizes.xl} />
                  <Text size="sm">XL ({iconSizes.xl}px)</Text>
                </div>
              </Flex>
            </div>

            <div>
              <Text weight="medium" className="mb-3">
                Icon Collection
              </Text>
            </div>

            <div>
              <Text weight="medium" className="mb-3">
                Usage Example
              </Text>
              <Card className="p-4">
                <Text size="sm" color="muted" className="mb-2">
                  Import icons from the UI package:
                </Text>
                <div className="bg-muted p-3 rounded-md font-mono text-sm">
                  <Text size="sm">{`import { House, User, Settings, TagBadge } from '@chatsdet/ui';`}</Text>
                </div>
                <Text size="sm" color="muted" className="mt-2 mb-2">
                  Use with size prop:
                </Text>
                <div className="bg-muted p-3 rounded-md font-mono text-sm">
                  <Text size="sm">{`<House size={20} />`}</Text>
                </div>
              </Card>
            </div>
          </div>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Navigation Components</Heading>
          <Text color="muted" className="mt-2 mb-4">
            Side navigation components with icons, badges, and active states
          </Text>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Full Side Navigation Example */}
            <Card className="h-fit">
              <CardHeader>
                <Heading level={4}>Complete Side Navigation</Heading>
                <Text size="sm" color="muted">
                  With groups, icons, and badges
                </Text>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <SideNav className="h-80 w-full">
                    <SideNavGroup title="Main">
                      <SideNavLink
                        href="#"
                        isActive
                        icon={<House size={18} />}
                        badge={<TagBadge name="12" color="gray" hideDot size="sm" />}
                      >
                        Dashboard
                      </SideNavLink>
                      <SideNavLink href="#" icon={<ChartBar size={18} />}>
                        Analytics
                      </SideNavLink>
                      <SideNavLink
                        href="#"
                        icon={<User size={18} />}
                        badge={<TagBadge name="3" color="yellow" hideDot size="sm" />}
                      >
                        Users
                      </SideNavLink>
                    </SideNavGroup>

                    <SideNavGroup title="Content">
                      <SideNavLink href="#" icon={<Edit size={18} />}>
                        Posts
                      </SideNavLink>
                      <SideNavLink href="#" icon={<Edit size={18} />}>
                        Categories
                      </SideNavLink>
                      <SideNavLink
                        href="#"
                        icon={<ChatCircle size={18} />}
                        badge={<TagBadge name="New" color="red" hideDot size="sm" />}
                      >
                        Comments
                      </SideNavLink>
                    </SideNavGroup>

                    <SideNavGroup title="Settings">
                      <SideNavLink href="#" icon={<Settings size={18} />}>
                        General
                      </SideNavLink>
                      <SideNavLink href="#" icon={<User size={18} />}>
                        Security
                      </SideNavLink>
                    </SideNavGroup>
                  </SideNav>
                </div>
              </CardContent>
            </Card>

            {/* Individual Link Examples */}
            <Card className="h-fit">
              <CardHeader>
                <Heading level={4}>Link Variants</Heading>
                <Text size="sm" color="muted">
                  Different states and configurations
                </Text>
              </CardHeader>
              <CardContent>
                <Stack gap="sm">
                  <Text weight="medium" size="sm">
                    Default Variant:
                  </Text>
                  <div className="space-y-1 p-3 border rounded-lg">
                    <SideNavLink href="#" icon={<ChartBar size={16} />}>
                      Normal Link
                    </SideNavLink>
                    <SideNavLink href="#" icon={<ChartBar size={16} />} isActive>
                      Active Link
                    </SideNavLink>
                    <SideNavLink
                      href="#"
                      icon={<ChartBar size={16} />}
                      badge={<TagBadge name="99+" color="blue" hideDot size="sm" />}
                    >
                      With Badge
                    </SideNavLink>
                  </div>

                  <Text weight="medium" size="sm" className="mt-4">
                    Compact Variant:
                  </Text>
                  <div className="space-y-1 p-3 border rounded-lg">
                    <SideNavLink href="#" icon={<MagnifyingGlass size={14} />} variant="compact">
                      Search
                    </SideNavLink>
                    <SideNavLink href="#" icon={<Calendar size={14} />} variant="compact" isActive>
                      Tasks
                    </SideNavLink>
                    <SideNavLink
                      href="#"
                      icon={<Bell size={14} />}
                      variant="compact"
                      badge={<TagBadge name="5" color="blue" hideDot size="sm" />}
                    >
                      Inbox
                    </SideNavLink>
                  </div>
                </Stack>
              </CardContent>
            </Card>

            {/* Breadcrumbs Examples */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <Heading level={4}>Breadcrumbs</Heading>
                <Text>Navigation breadcrumbs for hierarchical navigation</Text>
              </CardHeader>
              <CardContent>
                <Stack gap="lg">
                  <div>
                    <Text size="sm" weight="medium" className="mb-2">
                      Basic Breadcrumbs
                    </Text>
                    <Breadcrumbs>
                      <Breadcrumb href="#">Home</Breadcrumb>
                      <Breadcrumb href="#">Dashboard</Breadcrumb>
                      <Breadcrumb href="#">Requirements</Breadcrumb>
                      <Breadcrumb>REQ-123</Breadcrumb>
                    </Breadcrumbs>
                  </div>

                  <div>
                    <Text size="sm" weight="medium" className="mb-2">
                      With Router Integration
                    </Text>
                    <Breadcrumbs>
                      <Breadcrumb onPress={() => alert('Navigate to Dashboard')}>
                        Dashboard
                      </Breadcrumb>
                      <Breadcrumb onPress={() => alert('Navigate to Projects')}>
                        Projects
                      </Breadcrumb>
                      <Breadcrumb onPress={() => alert('Navigate to Requirements')}>
                        Requirements
                      </Breadcrumb>
                      <Breadcrumb>Current Page</Breadcrumb>
                    </Breadcrumbs>
                  </div>

                  <div>
                    <Text size="sm" weight="medium" className="mb-2">
                      Custom Separator
                    </Text>
                    <Breadcrumbs separator="/">
                      <Breadcrumb href="#">Users</Breadcrumb>
                      <Breadcrumb href="#">john.doe</Breadcrumb>
                      <Breadcrumb>Profile</Breadcrumb>
                    </Breadcrumbs>
                  </div>

                  <div>
                    <Text size="sm" weight="medium" className="mb-2">
                      With Disabled State
                    </Text>
                    <Breadcrumbs>
                      <Breadcrumb href="#">Home</Breadcrumb>
                      <Breadcrumb href="#" isDisabled>
                        Restricted Area
                      </Breadcrumb>
                      <Breadcrumb>Current Page</Breadcrumb>
                    </Breadcrumbs>
                  </div>
                </Stack>
              </CardContent>
            </Card>
          </div>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Charts & Data Visualization</Heading>
          <Text color="muted" className="mt-2 mb-6">
            Interactive charts and gauges using Recharts with our warm sepia theme
          </Text>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Line Chart */}
            <Card>
              <CardHeader>
                <Heading level={4}>Revenue Trends</Heading>
                <Text size="sm" color="muted">
                  Monthly revenue over the past year
                </Text>
              </CardHeader>
              <CardContent>
                <LineChart
                  height={250}
                  data={[
                    { month: 'Jan', revenue: 45000, expenses: 32000 },
                    { month: 'Feb', revenue: 52000, expenses: 35000 },
                    { month: 'Mar', revenue: 48000, expenses: 33000 },
                    { month: 'Apr', revenue: 61000, expenses: 40000 },
                    { month: 'May', revenue: 55000, expenses: 38000 },
                    { month: 'Jun', revenue: 67000, expenses: 42000 },
                    { month: 'Jul', revenue: 72000, expenses: 45000 },
                    { month: 'Aug', revenue: 69000, expenses: 44000 },
                    { month: 'Sep', revenue: 78000, expenses: 48000 },
                    { month: 'Oct', revenue: 82000, expenses: 50000 },
                    { month: 'Nov', revenue: 85000, expenses: 52000 },
                    { month: 'Dec', revenue: 91000, expenses: 55000 },
                  ]}
                  xDataKey="month"
                  lines={[
                    { dataKey: 'revenue', name: 'Revenue' },
                    { dataKey: 'expenses', name: 'Expenses' },
                  ]}
                />
              </CardContent>
            </Card>

            {/* Bar Chart */}
            <Card>
              <CardHeader>
                <Heading level={4}>Team Performance</Heading>
                <Text size="sm" color="muted">
                  Tasks completed by team this quarter
                </Text>
              </CardHeader>
              <CardContent>
                <BarChart
                  height={250}
                  data={[
                    { team: 'Frontend', completed: 89, inProgress: 12 },
                    { team: 'Backend', completed: 76, inProgress: 18 },
                    { team: 'Design', completed: 65, inProgress: 8 },
                    { team: 'QA', completed: 92, inProgress: 15 },
                    { team: 'DevOps', completed: 58, inProgress: 6 },
                  ]}
                  xDataKey="team"
                  bars={[
                    { dataKey: 'completed', name: 'Completed' },
                    { dataKey: 'inProgress', name: 'In Progress' },
                  ]}
                />
              </CardContent>
            </Card>

            {/* Area Chart */}
            <Card>
              <CardHeader>
                <Heading level={4}>User Growth</Heading>
                <Text size="sm" color="muted">
                  Active users and new signups over time
                </Text>
              </CardHeader>
              <CardContent>
                <AreaChart
                  height={250}
                  data={[
                    { month: 'Jan', activeUsers: 12400, newSignups: 1200 },
                    { month: 'Feb', activeUsers: 13100, newSignups: 1350 },
                    { month: 'Mar', activeUsers: 14200, newSignups: 1180 },
                    { month: 'Apr', activeUsers: 15800, newSignups: 1420 },
                    { month: 'May', activeUsers: 16900, newSignups: 1680 },
                    { month: 'Jun', activeUsers: 18400, newSignups: 1920 },
                  ]}
                  xDataKey="month"
                  areas={[
                    { dataKey: 'activeUsers', name: 'Active Users' },
                    { dataKey: 'newSignups', name: 'New Signups' },
                  ]}
                />
              </CardContent>
            </Card>

            {/* Pie Chart */}
            <Card>
              <CardHeader>
                <Heading level={4}>Traffic Sources</Heading>
                <Text size="sm" color="muted">
                  Website traffic breakdown by source
                </Text>
              </CardHeader>
              <CardContent className="flex justify-center">
                <PieChart
                  height={250}
                  data={[
                    { name: 'Organic Search', value: 35 },
                    { name: 'Social Media', value: 25 },
                    { name: 'Direct', value: 20 },
                    { name: 'Email', value: 12 },
                    { name: 'Referral', value: 8 },
                  ]}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={90}
                />
              </CardContent>
            </Card>
          </div>

          {/* Gauge Charts Section */}
          <div className="mt-8">
            <Heading level={3} className="mb-4">
              Performance Gauges
            </Heading>
            <Text color="muted" className="mb-6">
              Chrome Lighthouse-style performance indicators
            </Text>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <Card className="text-center">
                <CardContent className="pt-6">
                  <GaugeChart
                    score={87}
                    label="Performance"
                    size="sm"
                    showScore={true}
                    showLabel={true}
                  />
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="pt-6">
                  <GaugeChart
                    score={94}
                    label="Accessibility"
                    size="sm"
                    showScore={true}
                    showLabel={true}
                  />
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="pt-6">
                  <GaugeChart
                    score={78}
                    label="Best Practices"
                    size="sm"
                    showScore={true}
                    showLabel={true}
                  />
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="pt-6">
                  <GaugeChart score={92} label="SEO" size="sm" showScore={true} showLabel={true} />
                </CardContent>
              </Card>
            </div>

            {/* Large Gauge Example */}
            <div className="mt-8 flex justify-center">
              <Card className="text-center">
                <CardHeader>
                  <Heading level={4}>Overall Score</Heading>
                  <Text size="sm" color="muted">
                    Composite performance rating
                  </Text>
                </CardHeader>
                <CardContent>
                  <GaugeChart
                    score={88}
                    label="System Health"
                    size="lg"
                    showScore={true}
                    showLabel={true}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Filter Toolbar Example</Heading>
          <Text color="muted" className="mt-2 mb-4">
            Pattern for pairing a search field, inline filter chips, and table metadata without a
            bespoke component.
          </Text>
          <Card>
            <CardHeader>
              <Heading level={4}>Table Filters</Heading>
              <Text size="sm" color="muted">
                Align search, reset, and edit actions with active filter chips and export controls.
              </Text>
            </CardHeader>
            <CardContent>
              <Stack gap="lg">
                <Flex align="center" gap="sm" wrap justify="between">
                  <TextField
                    aria-label="Demo table search"
                    placeholder="Search resources"
                    leftIcon={<MagnifyingGlass size={16} />}
                    className="w-full max-w-xl min-w-[260px]"
                  />
                  <Flex gap="sm" align="center" className="flex-1 justify-end min-w-[200px]">
                    {filterDemoChips.length > 0 ? (
                      <Button variant="ghost" size="sm" onPress={filterDemoReset}>
                        Reset filters
                      </Button>
                    ) : null}
                    <Button
                      variant={filterDemoChips.length > 0 ? 'primary' : 'secondary'}
                      size="sm"
                    >
                      {filterDemoChips.length > 0
                        ? `Edit filters (${filterDemoChips.length})`
                        : 'Add filters'}
                    </Button>
                  </Flex>
                </Flex>
                <Flex align="center" justify="between" gap="md" wrap>
                  <Flex align="center" gap="sm" wrap className="flex-1 min-w-[200px]">
                    <Text size="sm" className="text-muted-foreground font-medium whitespace-nowrap">
                      Active filters:
                    </Text>
                    {filterDemoChips.length > 0 ? (
                      <TagGroup
                        aria-label="Demo active filters"
                        items={filterDemoChips}
                        onRemove={filterDemoRemove}
                        className="flex-1"
                      />
                    ) : (
                      <Text size="sm" className="text-muted-foreground">
                        None
                      </Text>
                    )}
                  </Flex>
                  <Flex
                    align="center"
                    gap="sm"
                    className="text-sm text-muted-foreground whitespace-nowrap"
                  >
                    <Text size="sm" className="text-muted-foreground">
                      {filterDemoResults} {filterDemoResults === 1 ? 'result' : 'results'}
                      {filterDemoChips.length > 0
                        ? ` · ${filterDemoChips.length} ${filterDemoChips.length === 1 ? 'filter' : 'filters'}`
                        : ''}
                    </Text>
                    <TooltipTrigger delay={0} offset={8}>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        iconLeft={<Download size={16} />}
                        aria-label="Export CSV"
                      />
                      <Tooltip className="z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95">
                        Export CSV
                      </Tooltip>
                    </TooltipTrigger>
                  </Flex>
                </Flex>
              </Stack>
            </CardContent>
          </Card>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Tags</Heading>
          <Text color="muted" className="mt-2 mb-6">
            Colored tag badges for organizing and categorizing items. Distinct from Badge component
            with colored dot and tinted background.
          </Text>

          <div className="space-y-8">
            {/* TagBadge variants */}
            <Card>
              <CardHeader>
                <Heading level={4}>Tag Badge</Heading>
                <Text size="sm" color="muted">
                  Visual tag indicator with colored dot
                </Text>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <Text weight="medium" className="mb-3">
                      Color Palette
                    </Text>
                    <Flex gap="sm" wrap>
                      {TAG_COLOR_OPTIONS.map((option) => (
                        <TagBadge key={option.id} name={option.label} color={option.id} />
                      ))}
                    </Flex>
                  </div>
                  <div>
                    <Text weight="medium" className="mb-3">
                      Sizes
                    </Text>
                    <Flex gap="md" align="center">
                      <TagBadge name="Small" color="blue" size="sm" />
                      <TagBadge name="Medium" color="green" size="md" />
                      <TagBadge name="Large" color="purple" size="lg" />
                    </Flex>
                  </div>
                  <div>
                    <Text weight="medium" className="mb-3">
                      Removable
                    </Text>
                    <Flex gap="sm">
                      <TagBadge
                        name="Click X to remove"
                        color="red"
                        onRemove={() => toast.default('Tag removed!')}
                      />
                    </Flex>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* TagPicker */}
            <Card>
              <CardHeader>
                <Heading level={4}>Tag Picker</Heading>
                <Text size="sm" color="muted">
                  Multi-select with inline tag creation (uses in-memory state for demo)
                </Text>
              </CardHeader>
              <CardContent>
                <div className="max-w-md">
                  <TagPicker
                    label="Tags"
                    description="Select existing tags or create new ones"
                    tags={demoTags}
                    selectedTags={selectedDemoTags}
                    onChange={setSelectedDemoTags}
                    onCreateTag={handleCreateDemoTag}
                  />
                </div>
                {selectedDemoTags.length > 0 && (
                  <div className="mt-4 p-3 bg-muted rounded-md">
                    <Text size="sm" weight="medium">
                      Selected Tags:
                    </Text>
                    <Flex gap="sm" className="mt-2" wrap>
                      {selectedDemoTags.map((tag) => (
                        <TagBadge key={tag.id} name={tag.name} color={tag.color} />
                      ))}
                    </Flex>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Available Tags (showing in-memory state) */}
            <Card>
              <CardHeader>
                <Heading level={4}>Available Tags (In-Memory State)</Heading>
                <Text size="sm" color="muted">
                  All tags created in this session - try creating new ones via the picker above
                </Text>
              </CardHeader>
              <CardContent>
                <Flex gap="sm" wrap>
                  {demoTags.map((tag) => (
                    <TagBadge key={tag.id} name={tag.name} color={tag.color} />
                  ))}
                </Flex>
              </CardContent>
            </Card>
          </div>
        </section>

        <Divider />

        <section>
          <Heading level={2}>Data Table</Heading>
          <Text color="muted" className="mt-2 mb-4">
            Advanced table with sorting, filtering, pagination, and resizable columns using TanStack
            Table + React Aria Components
          </Text>
          <Text size="sm" color="muted" className="mb-4">
            💡 Hover over column borders to see resize handles - drag to adjust column widths. Rows
            can expand inline for quick detail without leaving the list.
          </Text>

          <Card>
            <CardHeader>
              <Flex align="center" justify="between">
                <div>
                  <Heading level={4}>With Expander Column</Heading>
                  <Text size="sm" color="muted">
                    Explicit caret column; only expandable rows show the control.
                  </Text>
                </div>
                <TagBadge name="Single open" color="gray" hideDot />
              </Flex>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={columns}
                data={sampleData}
                searchable
                aria-label="Example users table"
                searchPlaceholder="Search users..."
                pageSize={3}
                expandableRows={{
                  render: (row) => {
                    const person = row.original;
                    return (
                      <Stack gap="sm">
                        <Text weight="medium">{person.summary}</Text>
                        <Flex gap="sm" wrap>
                          <TagBadge name={person.location} color="gray" hideDot />
                          <TagBadge name={person.team} color="gray" hideDot />
                          <TagBadge
                            name={<>Last active: {person.lastActive}</>}
                            color="gray"
                            hideDot
                          />
                        </Flex>
                        <div>
                          <Text size="sm" weight="medium">
                            Focus areas
                          </Text>
                          <Flex gap="sm" wrap className="mt-2">
                            {person.skills.map((skill) => (
                              <TagBadge name={skill} color="gray" hideDot key={skill} />
                            ))}
                          </Flex>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onPress={() => toast.default(`Viewing ${person.name}`)}
                        >
                          View profile
                        </Button>
                      </Stack>
                    );
                  },
                  getIsRowExpandable: (row) => row.original.status !== 'inactive',
                  single: true,
                }}
              />
            </CardContent>
          </Card>

          <Card className="mt-6">
            <CardHeader>
              <Flex align="center" justify="between">
                <div>
                  <Heading level={4}>Row-Click Expansion (No Expander Column)</Heading>
                  <Text size="sm" color="muted">
                    Use when you want the row itself to toggle expansion—keep the grid clean.
                  </Text>
                </div>
                <TagBadge name="Multi-open" color="gray" hideDot />
              </Flex>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={columns}
                data={sampleData}
                aria-label="Example users table row-click expand"
                pageSize={3}
                expandableRows={{
                  render: (row) => {
                    const person = row.original;
                    return (
                      <Stack gap="sm">
                        <Text weight="medium">{person.summary}</Text>
                        <Flex gap="sm" wrap>
                          <TagBadge name={person.location} color="gray" hideDot />
                          <TagBadge name={person.team} color="gray" hideDot />
                          <TagBadge
                            name={<>Last active: {person.lastActive}</>}
                            color="gray"
                            hideDot
                          />
                        </Flex>
                      </Stack>
                    );
                  },
                  expanderColumnPlacement: 'none',
                  getIsRowExpandable: (row) => row.original.status !== 'inactive',
                  single: false,
                }}
              />
            </CardContent>
          </Card>
        </section>
        {/* Rich Text Editor */}
        <RichTextEditorDemo />
      </Stack>
    </Box>
  );
}

const RTE_TOKEN_PATTERNS: TokenPattern[] = [
  { regex: MACRO_PATTERN, type: 'macro' },
  { regex: VARIABLE_PATTERN, type: 'variable' },
];

const RTE_TOKEN_COLORS = {
  variable: TOKEN_COLORS.variable,
  macro: TOKEN_COLORS.macro,
};

const DEMO_VARIABLES = [
  { name: 'dashboard_url', source: 'inputValue', description: 'URL of the dashboard' },
  { name: 'username', source: 'extracted', description: 'Logged-in user name' },
  { name: 'session_token', source: 'credential', description: 'Auth session token' },
];

const DEMO_MACRO_ITEMS: SuggestionItem[] = MACRO_CATALOG.map((m) => ({
  id: `macro:${m.name}`,
  label: `$${m.name}()`,
  kind: 'macro' as const,
  ...m,
}));

const DEMO_VARIABLE_ITEMS: SuggestionItem[] = DEMO_VARIABLES.map((v) => ({
  id: `var:${v.name}`,
  label: `$var.${v.name}`,
  kind: 'variable' as const,
  ...v,
}));

const DEMO_TRIGGER: SuggestionTrigger = {
  char: '$',
  allowSpaces: false,
  items: (query: string) => {
    const upper = query.toUpperCase();
    const lower = query.toLowerCase();

    if (lower.startsWith('var.')) {
      const varPrefix = lower.slice(4);
      return DEMO_VARIABLE_ITEMS.filter((v) =>
        (v.name as string).toLowerCase().startsWith(varPrefix)
      );
    }
    if ('var.'.startsWith(lower) && lower.length > 0) {
      return DEMO_VARIABLE_ITEMS;
    }

    const macros = DEMO_MACRO_ITEMS.filter((m) => (m.name as string).startsWith(upper));
    const vars = DEMO_VARIABLE_ITEMS.filter((v) =>
      (v.name as string).toLowerCase().startsWith(lower)
    );
    return [...macros, ...vars];
  },
  render: () => createMacroSuggestionRenderer(),
  onSelect: (item) => {
    if (item.kind === 'variable') {
      return { text: `$var.${item.name as string}`, tokenType: 'variable' };
    }
    const entry = item as SuggestionItem & { args: { name: string }[] };
    if (entry.args.length > 0) {
      return { plainText: `$${entry.name as string}()`, cursorOffset: 1 };
    }
    return { text: `$${entry.name as string}()`, tokenType: 'macro' };
  },
};

const DEMO_TRIGGERS = [DEMO_TRIGGER];

function RichTextEditorDemo() {
  const [singleLine, setSingleLine] = React.useState('Hello $EMAIL() welcome');
  const [multiLine, setMultiLine] = React.useState(
    'Navigate to $var.dashboard_url\nVerify $var.username is displayed'
  );
  const [empty, setEmpty] = React.useState('');

  return (
    <section>
      <Heading level={2} className="mb-4">
        Rich Text Editor
      </Heading>
      <Card>
        <CardContent>
          <Stack gap="lg">
            <RichTextEditor
              label="Single-line (with token pills)"
              placeholder="Type here… tokens like $EMAIL() appear as pills"
              value={singleLine}
              onChange={setSingleLine}
              tokenPatterns={RTE_TOKEN_PATTERNS}
              tokenColors={RTE_TOKEN_COLORS}
              triggers={DEMO_TRIGGERS}
            />

            <RichTextEditor
              label="Multi-line"
              placeholder="Multi-line editor with token support…"
              value={multiLine}
              onChange={setMultiLine}
              tokenPatterns={RTE_TOKEN_PATTERNS}
              tokenColors={RTE_TOKEN_COLORS}
              triggers={DEMO_TRIGGERS}
              multiline
              rows={4}
            />

            <RichTextEditor
              label="With placeholder"
              placeholder="Type $ for macros, $var. for variables"
              value={empty}
              onChange={setEmpty}
              tokenPatterns={RTE_TOKEN_PATTERNS}
              tokenColors={RTE_TOKEN_COLORS}
              triggers={DEMO_TRIGGERS}
            />

            <RichTextEditor
              label="Disabled"
              value="$EMAIL() cannot be edited"
              onChange={() => {}}
              tokenPatterns={RTE_TOKEN_PATTERNS}
              tokenColors={RTE_TOKEN_COLORS}
              isDisabled
            />

            <RichTextEditor
              label="With error"
              value="Invalid value"
              onChange={() => {}}
              tokenPatterns={RTE_TOKEN_PATTERNS}
              tokenColors={RTE_TOKEN_COLORS}
              errorMessage="This field has an error"
            />

            <Flex gap="md" className="pt-2">
              <Box className="flex-1 rounded-md bg-muted/50 p-3">
                <Text size="xs" className="font-medium text-muted-foreground mb-1">
                  Single-line value:
                </Text>
                <Text size="xs" className="font-mono break-all">
                  {singleLine || '(empty)'}
                </Text>
              </Box>
              <Box className="flex-1 rounded-md bg-muted/50 p-3">
                <Text size="xs" className="font-medium text-muted-foreground mb-1">
                  Multi-line value:
                </Text>
                <Text size="xs" className="font-mono whitespace-pre-wrap break-all">
                  {multiLine || '(empty)'}
                </Text>
              </Box>
            </Flex>
          </Stack>
        </CardContent>
      </Card>
    </section>
  );
}

/**
 * No providers here on purpose. `AppShell` already mounts `ThemeProvider`
 * above every route, and `main.tsx` mounts `ToastProvider`. This page used to
 * nest its own pair, which is what made the gallery unable to render the
 * cofactor theme: `ThemeProvider` targets `document.documentElement`
 * unconditionally, so a nested one doesn't scope anything — it just races the
 * outer one for the same element.
 */
export function UIDemo() {
  return <UIDemoContent />;
}
